import { getAuth, onAuthStateChanged } from "firebase/auth";
import { logging } from "../logger";

const proxyURL = '/.netlify/functions/api-proxy';

const auth = getAuth();

// Enhanced getIdToken with automatic retry for invalid anonymous tokens
export async function getIdToken(): Promise<string | null> {
  return new Promise(async (resolve) => {
    // If there's already a current user, return their token immediately
    if (auth.currentUser && !auth.currentUser.isAnonymous) {
      try {
        const token = await auth.currentUser.getIdToken();
        resolve(token);
        return;
      } catch (error) {
        logging.error('Error getting Firebase token:', {context: error});
        // Fall through to anonymous token
      }
    }

    // Otherwise, wait for auth state to load
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribe(); // Clean up the listener
      if (user && !user.isAnonymous) {
        try {
          // if user exists, just get their ID token
          const token = await user.getIdToken();
          resolve(token);
        } catch (error) {
          const anonymousToken = await getAnonymousToken();
          resolve(anonymousToken);
        }
      } else {
        // For anonymous or no user, get anonymous token
        const anonymousToken = await getAnonymousToken();
        resolve(anonymousToken);
      }
    });
  });
}

// Helper function to make proxy requests without an existing Bearer token
const makeAnonymousProxyRequest = async (
  endpoint: string,
  method: string = 'GET',
  data?: any,
  authUserId?: string
): Promise<Response> => {
  const requestBody = {
    endpoint,
    method,
    data,
    authUserId,
  };

  const requestOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody)
  };

  try {
    const response = await fetch(proxyURL, requestOptions);

    // Handle timeout responses specifically
    if (response.status === 408) {
      const errorData = await response.json();
      logging.warning(`Request timeout for ${endpoint}:`, {context: {message: errorData.message}});

      throw new Error(`Request timed out: ${errorData.message}`);
    }

    return response;
  } catch (error) {
    // If it's a fetch error (network issues, etc.)
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network error - please check your connection and try again');
    }

    // Re-throw other errors as-is
    throw error;
  }
};


async function getAnonymousToken(): Promise<string | null> {
  // Check if we have a stored token first
  const storedToken = getStoredAnonymousToken();
  if (storedToken) {
    return storedToken;
  }

  // If no stored token, fetch a new one
  try {
    const response = await makeAnonymousProxyRequest('api/auth/anonymous-token', 'POST');

    if (!response.ok) {
      const responseText = await response.text().catch(e => `Could not read response body: ${e.message}`);
      logging.error('Failed to get anonymous token:', {context: {status: response.status, body: responseText}});
      return null;
    }

    const data: AnonymousTokenResponse = await response.json();

    // Extract token and expiration info from structured response
    const newToken = data.anonymous_token;
    const expiresAt = data.expires_at;

    if (newToken) {
      setStoredAnonymousToken(newToken, expiresAt);
    }

    return newToken;
  } catch (error) {
    logging.error('Error getting anonymous token:', {context: {error}});
    return null;
  }
}

// Storage keys for anonymous token and metadata
const ANONYMOUS_TOKEN_KEY = 'session_token';
const ANONYMOUS_TOKEN_EXPIRES_KEY = 'session_token_expires';

// Interface for anonymous token response
interface AnonymousTokenResponse {
  anonymous_token: string;
  token_type: string;
  expires_in: number;
  expires_at: string;
}

// Helper functions for token storage with expiration
function getStoredAnonymousToken(): string | null {
  try {
    const token = sessionStorage.getItem(ANONYMOUS_TOKEN_KEY);
    const expiresAt = sessionStorage.getItem(ANONYMOUS_TOKEN_EXPIRES_KEY);

    // If we have a token but no expiration timestamp, treat as expired
    if (token && !expiresAt) {
      clearStoredAnonymousToken();
      return null;
    }

    // If we have both token and expiration, check if still valid
    if (token && expiresAt) {
      const expirationTime = new Date(expiresAt).getTime();
      const currentTime = Date.now();

      // Check if token is expired (with 5 minute buffer)
      if (currentTime >= expirationTime - (5 * 60 * 1000)) {
        clearStoredAnonymousToken();
        return null;
      }

      return token;
    }

    // No token stored
    return null;
  } catch (error) {
    logging.warning('Failed to read from sessionStorage:', {context: {error}});
    return null;
  }
}

function setStoredAnonymousToken(token: string, expiresAt?: string): void {
  try {
    sessionStorage.setItem(ANONYMOUS_TOKEN_KEY, token);
    if (expiresAt) {
      sessionStorage.setItem(ANONYMOUS_TOKEN_EXPIRES_KEY, expiresAt);
    }
  } catch (error) {
    logging.warning('Failed to write to sessionStorage:', {context: {error}});
  }
}

export function clearStoredAnonymousToken(): void {
  try {
    sessionStorage.removeItem(ANONYMOUS_TOKEN_KEY);
    sessionStorage.removeItem(ANONYMOUS_TOKEN_EXPIRES_KEY);
  } catch (error) {
    logging.warning('Failed to clear sessionStorage:', {context: {error}});
  }
}
